import { createElement } from 'react';

import { getDocument } from '@atlaskit/browser-apis';
import type { PortalProviderAPI } from '@atlaskit/editor-common/portal';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { Decoration, type DecorationSet } from '@atlaskit/editor-prosemirror/view';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { MalleableUiPlugin } from '../malleableUiPluginType';
import { RemixButtonDecoration } from '../ui/components/RemixButtonDecoration';

const TYPE_REMIX_BUTTON = 'REMIX_BUTTON' as const;

export const findRemixButtonDecoration = (
	decorations: DecorationSet,
	from?: number,
	to?: number,
) => {
	return decorations.find(from, to, (spec) => spec.type === TYPE_REMIX_BUTTON);
};

type RemixButtonDecorationParams = {
	anchorName: string;
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	nodeType: string;
	nodeViewPortalProviderAPI: PortalProviderAPI;
	rootAnchorName?: string;
	rootNodeType?: string;
	rootPos: number;
};

/** Right-edge Remix button: same gutter as left controls (side: -4) but positioned at block right edge. */
export const remixButtonDecoration = ({
	api,
	rootPos,
	anchorName,
	nodeType,
	nodeViewPortalProviderAPI,
	rootAnchorName,
	rootNodeType,
}: RemixButtonDecorationParams) => {
	const key = crypto.randomUUID();
	const widgetSpec = {
		side: -4,
		type: TYPE_REMIX_BUTTON,
		destroy: (_: Node) => {
			if (fg('platform_editor_fix_widget_destroy')) {
				nodeViewPortalProviderAPI.remove(key);
			}
		},
	};

	return Decoration.widget(
		rootPos,
		(view, getPos) => {
			const doc = getDocument();
			if (!doc) {
				throw new Error('Document not available');
			}
			const element = doc.createElement('span');
			element.style.display = 'block';
			element.contentEditable = 'false';
			if (expValEquals('confluence_remix_icon_right_side', 'isEnabled', true)) {
				// Generic right-edge control attribute for block-controls hover area (Remix-specific: data-blocks-remix-button-*)
				element.setAttribute('data-blocks-right-edge-button-container', 'true');
				element.setAttribute('data-blocks-right-edge-button-anchor', rootAnchorName ?? anchorName);
				element.setAttribute('data-blocks-remix-button-container', 'true');
				element.setAttribute('data-blocks-remix-button-anchor', rootAnchorName ?? anchorName);
			}
			element.setAttribute('data-testid', 'block-ctrl-remix-button-container');

			nodeViewPortalProviderAPI.render(
				() =>
					createElement(RemixButtonDecoration, {
						api,
						getPos,
						view,
						nodeType,
						anchorName,
						rootAnchorName,
						rootNodeType: rootNodeType ?? nodeType,
						rootPos,
					}),
				element,
				key,
				undefined,
				true,
			);

			return element;
		},
		widgetSpec,
	);
};
